import { inject, Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Store } from '@ngrx/store';
import { selectPrevLoggerMeasurementsRequest } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { contextMenuTodo } from '@app/main/main-content/context-menu/utils/context-menu-todo';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';
import { LeakDetectionSoundSignalService } from '@app/main/leak-detection/services/leak-detection-sound-signal/leak-detection-sound-signal.service';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { DialogSoundSignalRecordingStartTimesComponent } from '@app/main/leak-detection/context-menus/dialogs/dialog-sound-signal-recording-start-times/dialog-sound-signal-recording-start-times.component';
import { isLoggerNotAssignedToPipeNode } from '@app/core/methods/logger-pipe-assignment';
import { CONTEXT_MENU_WARNING_ITEM_CLASS } from 'src/theme/components/contextmenu/base-contextmenu-token';

@Injectable({ providedIn: 'root' })
export class LdContextMenuLoggerService {
  private readonly translateService = inject(TranslateService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);
  private readonly soundSignalService = inject(LeakDetectionSoundSignalService);
  private readonly dialogTrackerService = inject(DialogTrackerService);
  private readonly store = inject(Store);

  private readonly prevLoggerMeasurementsRequest = this.store.selectSignal(selectPrevLoggerMeasurementsRequest);

  public getMapItems(logger: ILdLogger, dispatch: LdCoordinatorDispatch): MenuItem[] {
    return this.getCommonItems(logger, LdActionPlace.Map, dispatch);
  }

  public getTableItems(
    logger: ILdLogger,
    cell: IMainTableCellContext | undefined,
    dispatch: LdCoordinatorDispatch
  ): MenuItem[] {
    return [
      ...this.contextMenuCopy.getCopyCellItems(cell),
      ...this.getCommonItems(logger, LdActionPlace.TabsPanel, dispatch),
    ];
  }

  private getCommonItems(logger: ILdLogger, place: LdActionPlace, dispatch: LdCoordinatorDispatch): MenuItem[] {
    const { deployment, measurement } = logger;
    const isUnassigned = isLoggerNotAssignedToPipeNode(deployment.adjustedCoordinates);

    const canAccessLoggerContextMenu = !!this.ngxPermissionsService.getPermission(
      PermissionName.LeakDetection_Read_LoggerContextMenu
    );

    const canAccessSoundSignalRecordingStartTimes = !!this.ngxPermissionsService.getPermission(
      PermissionName.LeakDetection_Read_SoundSignalRecordingStartTimes
    );

    return [
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-logger-noise-details__label`)),
        icon: 'icons/icon-chart.svg',
        command: () => dispatch({ type: LdActionType.LoggerDbClick, place, item: logger }),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-download-sound-file__label`)),
        icon: 'icons/icon-noise.svg',
        disabled: !measurement?.soundSignalUrl,
        command: () => this.soundSignalService.downloadByMeasurement(measurement),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-manual-correlation__label`)),
        icon: 'icons/icon-drop.svg',
        command: () => contextMenuTodo('Manual correlation', logger),
      },
      { separator: true },
      ...(isUnassigned
        ? [
            {
              label: this.translateService.instant(_(`@@leak-detection-context-menu-not-assigned-to-pipe-node__label`)),
              icon: 'icons/icon-warn.svg',
              disabled: true,
              styleClass: CONTEXT_MENU_WARNING_ITEM_CLASS,
            },
          ]
        : []),
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-assign-to-pipe-node__label`)),
        icon: 'icons/icon-assign-to-pipe-node.svg',
        disabled: false,
        // use disable for that
        iconStyle: { 'original-icon': true },
        command: () => contextMenuTodo('Assign to pipe node', logger),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-detach-from-pipe-node__label`)),
        icon: 'icons/icon-detach-from-pipe-node.svg',
        disabled: isUnassigned,
        iconStyle: { 'original-icon': true },
        command: () => contextMenuTodo('Detach from pipe node', logger),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-create-pipe__label`)),
        icon: 'icons/icon-create-pipe.svg',
        command: () => contextMenuTodo('Create pipe', logger),
      },
      this.contextMenuEvents.getCreateEventMenuItem(deployment.coordinates, place, dispatch),
      { separator: true },
      ...(canAccessSoundSignalRecordingStartTimes
        ? [
            {
              label: this.translateService.instant(
                _(`@@leak-detection-context-menu-sound-signal-recording-start-times__label`)
              ),
              icon: 'icons/icon-asset-identifier.svg',
              disabled: !measurement?.id,
              command: () => {
                const projectId = this.prevLoggerMeasurementsRequest()?.projectId;
                if (!measurement?.id || !projectId) return;

                this.dialogTrackerService.regAndOpen(DialogSoundSignalRecordingStartTimesComponent, {
                  ...this.dialogTrackerService.getBaseDialogConfig(),
                  width: '25rem',
                  height: '21.875rem',
                  breakpoints: null,
                  data: {
                    serialNo: deployment.serialNo,
                    projectId,
                    loggerMeasurementId: measurement.id,
                  },
                });
              },
            },
          ]
        : []),
      ...(canAccessLoggerContextMenu
        ? [
            {
              label: this.translateService.instant(_(`@@context-menu-show-photos__label`)),
              icon: 'icons/icon-picture.svg',
              command: () => dispatch({ type: LdActionType.ShowPhotos, place, item: logger }),
            },
          ]
        : []),
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
        icon: 'icons/icon-show-on-street.svg',
        iconStyle: { 'original-icon': true },
        command: () => dispatch({ type: LdActionType.ShowInStreetView, place, position: deployment.coordinates }),
      },
    ];
  }
}
